import { consentDocument } from "../consentDocument"
import { ConsentType, useSignConsent } from "../hooks"
import { Box, Divider, ForwardIcon, Typography, dayjs } from "@carrotfertility/carotene-core"
import { Form, FormAlert, FormButton, FormCheckbox, FormTextField } from "@carrotfertility/carotene-core-x"
import React from "react"
import { BookingBreadcrumbs } from "../Breadcrumbs"

type ConsentFormValues = {
  telehealthConsent: boolean
  healthInfoAuthorization: boolean
  // Typed-name signature. Gating only (MEM-755): required to submit, but never sent or stored.
  signature: string
}

type ConsentPageProps = {
  // The consents the member still needs to sign (from getConsentStatus) — recorded (by type) on submit.
  outstandingConsents: ConsentType[]
  onContinue: () => void
  onReturnToProviderResults?: () => void
}

const { telehealth_informed_consent: telehealthConsent, health_information_sharing: healthInfo } =
  consentDocument.consents

// Literal strings (no react-intl): this package isn't in the intl extraction pipeline and the
// experience is English-only for the foreseeable future. All copy comes from consentDocument.
export function ConsentPage({
  outstandingConsents,
  onContinue,
  onReturnToProviderResults
}: ConsentPageProps): JSX.Element {
  const signConsent = useSignConsent()

  // Render only the consents the member still needs to sign — one they already hold validly shows
  // neither its copy nor its checkbox. The typed-name signature ratifies the telehealth informed
  // consent + consent to treat (that's what its attestation says), so it appears only when that
  // consent is outstanding; health-info on its own is just a checkbox. Unrendered form fields aren't
  // registered, so they don't gate submit.
  const outstanding = new Set(outstandingConsents)
  const showTelehealthConsent = outstanding.has("telehealth_informed_consent")
  const showHealthInfoConsent = outstanding.has("health_information_sharing")
  const sections = [
    ...(showTelehealthConsent ? [telehealthConsent.section] : []),
    ...(showHealthInfoConsent ? [healthInfo.section] : [])
  ]

  // The form submits once every rendered required control is filled; by here consent is given —
  // record the outstanding consent types and advance only if it succeeds. Use the non-async `mutate`:
  // errors surface via signConsent.isError below (and would otherwise become an unhandled rejection,
  // since RHF's handleSubmit awaits this and there's no .catch). The typed name gates the form but is
  // never sent — the session is the "who".
  const handleSubmit = () => signConsent.mutate(outstandingConsents, { onSuccess: onContinue })

  // Today, long form (e.g. "August 17, 2026"), for the "Signed on" line — display only. This is NOT
  // the recorded signing time and is never sent: the authoritative signedAt is generated server-side
  // when the consent is persisted (MEM-756), so the client's clock is never the source of truth.
  // Hence it stays at render (the ceremony line needs it) rather than moving into handleSubmit.
  const signedOn = dayjs().format("MMM D, YYYY")

  return (
    <Form<ConsentFormValues>
      onSubmit={handleSubmit}
      defaultValues={{ telehealthConsent: false, healthInfoAuthorization: false, signature: "" }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(theme.tokens.spacing.lg),
          maxInlineSize: 640
        })}
      >
        <BookingBreadcrumbs onReturnToSearchResults={onReturnToProviderResults} />

        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
        >
          <Typography variant="h2" component="h1">
            {consentDocument.title}
          </Typography>
          <Typography>{consentDocument.intro}</Typography>
        </Box>

        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.md) })}
        >
          {sections.map((section) => (
            <Box
              key={section.heading}
              component="section"
              sx={(theme) => ({
                display: "flex",
                flexDirection: "column",
                gap: theme.spacing(theme.tokens.spacing.xs)
              })}
            >
              <Typography variant="h5" component="h2">
                {section.heading}
              </Typography>
              <Typography>{section.body}</Typography>
            </Box>
          ))}
        </Box>

        <Divider />

        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
        >
          {showTelehealthConsent && (
            <FormCheckbox
              name="telehealthConsent"
              label={telehealthConsent.clause.label}
              controllerProps={{ rules: { required: telehealthConsent.clause.requiredMessage } }}
            />
          )}
          {showHealthInfoConsent && (
            <FormCheckbox
              name="healthInfoAuthorization"
              label={healthInfo.clause.label}
              controllerProps={{ rules: { required: healthInfo.clause.requiredMessage } }}
            />
          )}
        </Box>

        {showTelehealthConsent && (
          <Box
            component="section"
            sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.md) })}
          >
            <Typography>{consentDocument.signature.attestation}</Typography>
            <FormTextField
              name="signature"
              label={consentDocument.signature.label}
              helperText={consentDocument.signature.helper}
              registerOptions={{
                required: consentDocument.signature.requiredMessage,
                // React Hook Form's `required` treats "   " as filled; the typed name is the
                // e-signature, so reject whitespace-only.
                validate: (value: string) => value.trim().length > 0 || consentDocument.signature.requiredMessage
              }}
            />
            <Box sx={(theme) => ({ display: "flex", gap: theme.spacing(theme.tokens.spacing.xxxl) })}>
              <Typography variant="body1" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
                Signed on
              </Typography>
              <Typography variant="body1">{signedOn}</Typography>
            </Box>
          </Box>
        )}

        {signConsent.isError && (
          <FormAlert severity="error">Something went wrong saving your consent. Please try again.</FormAlert>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <FormButton type="submit" color="secondary" endIcon={<ForwardIcon />}>
            Continue
          </FormButton>
        </Box>
      </Box>
    </Form>
  )
}
